import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import {
  Loader2, Trash2, Plus, Save, ArrowLeft, Lock, Bot,
  Brain, ThumbsUp, ChevronDown, ChevronUp, X,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { isFacadeServiceSlug } from "@/config/serviceCatalog";
import { lovable } from "@/integrations/lovable";

const ADMIN_EMAILS = ["info@cenik.ai", "adam.cernohous@gmail.com"];

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) return session.access_token;
  const { data: { session: fresh } } = await supabase.auth.refreshSession();
  return fresh?.access_token ?? null;
}

interface Service {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  keywords: string[];
  query_count: number;
  status: "active" | "candidate" | "rejected" | "queued";
  created_by_ai: boolean;
  created_at: string;
}

interface CostField {
  key: string;
  label: string;
  icon: string;
  hint?: string | null;
}

interface QuoteConfig {
  id: string;
  service_id: string;
  cost_fields: CostField[];
  show_profit: boolean;
  show_vat: boolean;
  unit_label: string;
  status: "draft" | "approved";
  created_at: string;
  updated_at: string;
}

interface ServiceKeyword {
  id: string;
  service_id: string;
  keyword: string;
  status: "approved" | "suggested" | "rejected";
  source: string;
  weight: number;
  match_count: number;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
}

interface AiLearning {
  id: string;
  type: string;
  topic: string;
  content: string;
  source: string;
  engine_id: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export default function AdminServiceDetail() {
  const { t } = useI18n();
  const { slug } = useParams<{ slug: string }>();
  const { user, isReady } = useAuth();
  const navigate = useNavigate();

  // Auth
  const [adminAuthed, setAdminAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Data
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState<Service | null>(null);
  const [quoteConfig, setQuoteConfig] = useState<QuoteConfig | null>(null);
  const [learnings, setLearnings] = useState<AiLearning[]>([]);

  // Keywords (living dictionary)
  const [serviceKeywords, setServiceKeywords] = useState<ServiceKeyword[]>([]);
  const [newKeywordText, setNewKeywordText] = useState("");
  const [newKeywordWeight, setNewKeywordWeight] = useState(1.0);
  const [showRejectedKeywords, setShowRejectedKeywords] = useState(false);

  // Service edit
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editKeywords, setEditKeywords] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingService, setSavingService] = useState(false);

  // Quote config editing
  const [editingConfigFields, setEditingConfigFields] = useState<CostField[]>([]);
  const [editingConfigShowProfit, setEditingConfigShowProfit] = useState(true);
  const [editingConfigShowVat, setEditingConfigShowVat] = useState(true);
  const [editingConfigUnitLabel, setEditingConfigUnitLabel] = useState("m²");
  const [generatingConfig, setGeneratingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Learning
  const [showNewLearning, setShowNewLearning] = useState(false);
  const [newLearningType, setNewLearningType] = useState("correction");
  const [newLearningTopic, setNewLearningTopic] = useState("");
  const [newLearningContent, setNewLearningContent] = useState("");
  const [expandedLearning, setExpandedLearning] = useState<string | null>(null);

  // Auth check
  useEffect(() => {
    if (!isReady) return;
    if (user && ADMIN_EMAILS.includes(user.email || "")) {
      setAdminAuthed(true);
    }
  }, [isReady, user]);

  // Load data
  useEffect(() => {
    if (!adminAuthed || !slug) return;
    loadServiceData();
  }, [adminAuthed, slug]);

  const loadServiceData = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        setAdminAuthed(false);
        toast.error(t("admin.sessionExpired"));
        return;
      }
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (resp.status === 401 || resp.status === 403) {
        setAdminAuthed(false);
        return;
      }
      if (!resp.ok) throw new Error("Failed");
      const data = await resp.json();

      const svc = (data.services || []).find((s: Service) => s.slug === slug);
      if (!svc) {
        toast.error(t("admin.label.serviceNotFound"));
        navigate("/admin-secret?tab=landing");
        return;
      }
      setService(svc);
      setEditName(svc.name);
      setEditSlug(svc.slug);
      setEditKeywords((svc.keywords || []).join(", "));
      setEditDescription(svc.description || "");

      const cfg = (data.quoteConfigs || []).find((c: QuoteConfig) => c.service_id === svc.id) || null;
      setQuoteConfig(cfg);
      if (cfg) {
        setEditingConfigFields(cfg.cost_fields);
        setEditingConfigShowProfit(cfg.show_profit);
        setEditingConfigShowVat(cfg.show_vat);
        setEditingConfigUnitLabel(cfg.unit_label);
      }

      setLearnings((data.learnings || []).filter((l: AiLearning) => l.engine_id === slug));

      // Load keywords for this service
      setServiceKeywords((data.serviceKeywords || []).filter((k: ServiceKeyword) => k.service_id === svc.id));
    } catch {
      toast.error(t("admin.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Admin login
  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    if (!ADMIN_EMAILS.includes(email)) {
      setLoginLoading(false);
      toast.error(t("admin.noPermission"));
      return;
    }
    await supabase.auth.signOut();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoginLoading(false);
    if (error) {
      toast.error(t("admin.loginFailed") + ": " + error.message);
      return;
    }
    setAdminAuthed(true);
  };

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    const { error } = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/admin-secret",
    });
    if (error) {
      setLoginLoading(false);
      toast.error(t("admin.googleLoginFailed") + ": " + error.message);
    }
  };

  // --- Service edit handler ---
  const handleSaveService = async () => {
    if (!service) return;
    setSavingService(true);
    try {
      const token = await getToken();
      const keywords = editKeywords.split(",").map(k => k.trim()).filter(Boolean);
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_service", id: service.id, name: editName, slug: editSlug, keywords, description: editDescription || null }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) throw new Error("Failed");
      setService(prev => prev ? { ...prev, name: editName, slug: editSlug, keywords, description: editDescription || null } : prev);
      toast.success(t("admin.toast.serviceSaved"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    } finally {
      setSavingService(false);
    }
  };

  // --- Quote config handlers ---
  const handleGenerateQuoteConfig = async () => {
    if (!service) return;
    setGeneratingConfig(true);
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "generate_quote_config", serviceId: service.id }),
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) { toast.error(t("admin.toast.generateFailed")); return; }
      setQuoteConfig(result.config);
      setEditingConfigFields(result.config.cost_fields);
      setEditingConfigShowProfit(result.config.show_profit);
      setEditingConfigShowVat(result.config.show_vat);
      setEditingConfigUnitLabel(result.config.unit_label);
      toast.success(t("admin.toast.aiSuggestedForm"));
    } catch {
      toast.error(t("admin.toast.generateFailed"));
    } finally {
      setGeneratingConfig(false);
    }
  };

  const handleSaveQuoteConfig = async () => {
    if (!service) return;
    setSavingConfig(true);
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save_quote_config",
          serviceId: service.id,
          cost_fields: editingConfigFields,
          show_profit: editingConfigShowProfit,
          show_vat: editingConfigShowVat,
          unit_label: editingConfigUnitLabel,
        }),
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) { toast.error(t("admin.toast.saveFailed")); return; }
      setQuoteConfig(result.config);
      toast.success(t("admin.toast.saved"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleApproveQuoteConfig = async () => {
    if (!service) return;
    setSavingConfig(true);
    try {
      const token = await getToken();
      // Save first
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save_quote_config",
          serviceId: service.id,
          cost_fields: editingConfigFields,
          show_profit: editingConfigShowProfit,
          show_vat: editingConfigShowVat,
          unit_label: editingConfigUnitLabel,
        }),
      });
      // Then approve
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve_quote_config", serviceId: service.id }),
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) { toast.error(t("admin.toast.approveFailed")); return; }
      setQuoteConfig(result.config);
      toast.success(t("admin.toast.formApproved"));
    } catch {
      toast.error(t("admin.toast.approveFailed"));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleUnapproveQuoteConfig = async () => {
    if (!service) return;
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "unapprove_quote_config", serviceId: service.id }),
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) { toast.error(t("admin.toast.operationFailed")); return; }
      setQuoteConfig(result.config);
      setEditingConfigFields(result.config.cost_fields);
      setEditingConfigShowProfit(result.config.show_profit);
      setEditingConfigShowVat(result.config.show_vat);
      setEditingConfigUnitLabel(result.config.unit_label);
      toast.success(t("admin.toast.formToDraft"));
    } catch {
      toast.error(t("admin.toast.operationFailed"));
    }
  };

  // --- Keyword handlers ---
  const handleApproveKeyword = async (keywordId: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve_keyword", keywordId }),
      });
      if (!resp.ok) throw new Error("Failed");
      setServiceKeywords(prev => prev.map(k => k.id === keywordId ? { ...k, status: "approved", reviewed_at: new Date().toISOString() } : k));
      toast.success(t("admin.toast.keywordApproved"));
    } catch {
      toast.error(t("admin.toast.operationFailed"));
    }
  };

  const handleRejectKeyword = async (keywordId: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reject_keyword", keywordId }),
      });
      if (!resp.ok) throw new Error("Failed");
      setServiceKeywords(prev => prev.map(k => k.id === keywordId ? { ...k, status: "rejected", reviewed_at: new Date().toISOString() } : k));
      toast.success(t("admin.toast.keywordRejected"));
    } catch {
      toast.error(t("admin.toast.operationFailed"));
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeywordText.trim() || !service) return;
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "add_keyword", serviceId: service.id, keyword: newKeywordText.trim(), weight: newKeywordWeight }),
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) throw new Error("Failed");
      if (result.keyword) {
        setServiceKeywords(prev => {
          const exists = prev.find(k => k.id === result.keyword.id);
          if (exists) return prev.map(k => k.id === result.keyword.id ? result.keyword : k);
          return [...prev, result.keyword];
        });
      }
      setNewKeywordText("");
      setNewKeywordWeight(1.0);
      toast.success(t("admin.toast.keywordAdded"));
    } catch {
      toast.error(t("admin.toast.addFailed"));
    }
  };

  const handleDeleteKeyword = async (keywordId: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete_keyword", keywordId }),
      });
      if (!resp.ok) throw new Error("Failed");
      setServiceKeywords(prev => prev.filter(k => k.id !== keywordId));
      toast.success(t("admin.toast.keywordDeleted"));
    } catch {
      toast.error(t("admin.toast.deleteFailed"));
    }
  };

  const handleUpdateKeywordWeight = async (keywordId: string, weight: number) => {
    try {
      const token = await getToken();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_keyword_weight", keywordId, weight }),
      });
      setServiceKeywords(prev => prev.map(k => k.id === keywordId ? { ...k, weight } : k));
    } catch {
      toast.error(t("admin.toast.operationFailed"));
    }
  };

  // --- Learning handlers ---
  const handleCreateLearning = async () => {
    if (!newLearningTopic.trim() || !newLearningContent.trim()) {
      toast.error(t("admin.toast.fillTopicContent"));
      return;
    }
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create_learning",
          entry: { type: newLearningType, topic: newLearningTopic, content: newLearningContent, source: "admin", engine_id: slug || null },
        }),
      });
      if (!resp.ok) throw new Error("Create failed");
      const result = await resp.json();
      if (result.data) setLearnings(prev => [...prev, result.data]);
      setNewLearningTopic("");
      setNewLearningContent("");
      setShowNewLearning(false);
      toast.success(t("admin.toast.learningAdded"));
    } catch {
      toast.error(t("admin.toast.addFailed"));
    }
  };

  const handleToggleLearning = async (entry: AiLearning) => {
    try {
      const token = await getToken();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "update_learning",
          learningId: entry.id,
          updates: { is_active: !entry.is_active },
        }),
      });
      setLearnings(prev => prev.map(l => l.id === entry.id ? { ...l, is_active: !l.is_active } : l));
      toast.success(t(entry.is_active ? "admin.toast.deactivated" : "admin.toast.activated"));
    } catch {
      toast.error(t("admin.toast.error"));
    }
  };

  const handleDeleteLearning = async (id: string) => {
    if (!confirm(t("admin.confirm.deleteLearning"))) return;
    try {
      const token = await getToken();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete_learning", id }),
      });
      setLearnings(prev => prev.filter(l => l.id !== id));
      toast.success(t("admin.toast.deleted"));
    } catch {
      toast.error(t("admin.toast.deleteFailed"));
    }
  };

  const handleServiceStatus = async (status: "active" | "candidate" | "rejected" | "queued") => {
    if (!service) return;
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_service_status", id: service.id, status }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) throw new Error("Failed");
      setService(prev => prev ? { ...prev, status } : prev);
      toast.success(
        status === "active" ? t("admin.toast.serviceActivated") :
        status === "rejected" ? t("admin.toast.serviceRejected") :
        status === "queued" ? t("admin.toast.movedToQueue") :
        t("admin.toast.movedToCandidate")
      );
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  // --- Login screen ---
  if (!adminAuthed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Lock className="w-6 h-6 text-primary" />
              </div>
            </div>
            <h1 className="text-xl font-bold text-foreground">{t("admin.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("admin.loginPrompt")}</p>
          </div>
          <button
            onClick={handleGoogleLogin}
            disabled={loginLoading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm font-semibold hover:bg-muted transition-colors disabled:opacity-50"
          >
            {loginLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t("admin.googleLogin")}
              </>
            )}
          </button>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" />
            <span>{t("admin.orPassword")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("admin.email")}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground outline-none focus:ring-2 focus:ring-ring"
                placeholder="admin@example.com" required autoFocus />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("admin.password")}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••" required />
            </div>
            <button type="submit" disabled={loginLoading}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("admin.loginButton")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">{t("admin.label.serviceNotFound")}</p>
          <button onClick={() => navigate("/admin-secret?tab=landing")}
            className="text-sm text-primary hover:underline">
            {t("admin.btn.backToOverview")}
          </button>
        </div>
      </div>
    );
  }

  const isFasadyService = isFacadeServiceSlug(service.slug);
  const modules = isFasadyService
    ? [
        { key: "walls", icon: "🧱", label: t("admin.module.walls"), desc: t("admin.module.wallsDesc"), badgeCls: "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800" },
        { key: "material", icon: "📦", label: t("admin.module.material"), desc: t("admin.module.materialDesc"), badgeCls: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800" },
        { key: "quote_builder", icon: "🧮", label: t("admin.module.quoteCalc"), desc: t("admin.module.quoteCalcDesc"), badgeCls: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800" },
      ]
    : [
        { key: "basic_inquiry", icon: "📋", label: t("admin.module.generalInquiry"), desc: t("admin.module.generalInquiryDesc"), badgeCls: "bg-slate-100 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800" },
        { key: "simple_quote", icon: "💰", label: t("admin.module.simpleQuote"), desc: t("admin.module.simpleQuoteDesc"), badgeCls: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800" },
      ];

  const isEditing = quoteConfig && quoteConfig.status === "draft";
  const isApproved = quoteConfig && quoteConfig.status === "approved";
  const fields = editingConfigFields.length > 0 ? editingConfigFields : (quoteConfig?.cost_fields || []);

  const statusBadge = {
    active: { label: t("admin.status.active"), cls: "bg-green-100 text-green-700 border-green-200" },
    candidate: { label: t("admin.status.candidate"), cls: "bg-amber-100 text-amber-700 border-amber-200" },
    queued: { label: t("admin.status.queued"), cls: "bg-slate-100 text-slate-700 border-slate-200" },
    rejected: { label: t("admin.status.rejected"), cls: "bg-red-100 text-red-700 border-red-200" },
  }[service.status];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin-secret?tab=landing")}
            className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground">{service.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono text-xs text-muted-foreground">{service.slug}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusBadge.cls}`}>
                {statusBadge.label}
              </span>
              {service.created_by_ai && (
                <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">AI</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-green-600">{service.query_count}</span>
          <span className="text-xs text-muted-foreground">{service.query_count === 1 ? t("admin.label.query1") : service.query_count < 5 ? t("admin.label.query2to4") : t("admin.label.query5plus")}</span>
          <button onClick={loadServiceData} className="text-sm text-muted-foreground hover:text-foreground transition-colors ml-4">
            {t("admin.btn.refresh")}
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6 space-y-8">

        {/* Editace služby */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">{t("admin.section.basicInfo")}</h2>
          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">{t("admin.label.name")}</label>
                <input
                  className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">{t("admin.label.slugUrl")}</label>
                <input
                  className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background font-mono"
                  value={editSlug}
                  onChange={e => setEditSlug(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("admin.label.keywordsComma")}</label>
              <input
                className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background"
                placeholder={t("admin.placeholder.keywordsExample")}
                value={editKeywords}
                onChange={e => setEditKeywords(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">{t("admin.label.descOptional")}</label>
              <input
                className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background"
                placeholder={t("admin.placeholder.serviceDesc")}
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveService}
                disabled={savingService}
                className="flex items-center gap-1.5 text-sm bg-primary text-primary-foreground rounded-lg px-4 py-1.5 disabled:opacity-50"
              >
                {savingService ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t("admin.btn.save")}
              </button>
            </div>
          </div>
        </section>

        {/* Klíčová slova (living dictionary) */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">{t("admin.section.keywordsDetection")}</h2>
          <p className="text-xs text-muted-foreground">
            {t("admin.section.keywordsHelp")}
          </p>

          {/* Approved keywords */}
          {(() => {
            const approved = serviceKeywords.filter(k => k.status === "approved").sort((a, b) => b.weight - a.weight);
            return approved.length > 0 ? (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-green-700 dark:text-green-400">{t("admin.label.approved")} ({approved.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {approved.map(k => (
                    <span key={k.id} className="inline-flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-full px-2.5 py-1 group">
                      <span className="font-medium">{k.keyword}</span>
                      <span className="text-[10px] text-green-600 dark:text-green-500">{k.weight.toFixed(1)}</span>
                      {k.match_count > 0 && <span className="text-[10px] text-green-500 dark:text-green-600">{k.match_count}x</span>}
                      <button onClick={() => handleDeleteKeyword(k.id)}
                        className="ml-0.5 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Suggested keywords */}
          {(() => {
            const suggested = serviceKeywords.filter(k => k.status === "suggested");
            return suggested.length > 0 ? (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400">{t("admin.label.suggested")} ({suggested.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggested.map(k => (
                    <span key={k.id} className="inline-flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-full px-2.5 py-1">
                      <span>{k.keyword}</span>
                      <span className="text-[10px] text-amber-500">{k.source}</span>
                      <button onClick={() => handleApproveKeyword(k.id)}
                        className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title={t("admin.btn.approve")}>
                        <ThumbsUp className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleRejectKeyword(k.id)}
                        className="text-red-400 hover:text-red-600" title={t("admin.btn.reject")}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Rejected keywords (collapsed) */}
          {(() => {
            const rejected = serviceKeywords.filter(k => k.status === "rejected");
            return rejected.length > 0 ? (
              <div>
                <button onClick={() => setShowRejectedKeywords(!showRejectedKeywords)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  {showRejectedKeywords ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {t("admin.label.rejectedKw")} ({rejected.length})
                </button>
                {showRejectedKeywords && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {rejected.map(k => (
                      <span key={k.id} className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full px-2.5 py-1 line-through">
                        {k.keyword}
                        <button onClick={() => handleApproveKeyword(k.id)}
                          className="text-green-500 hover:text-green-700 no-underline" title={t("admin.btn.approve")}>
                          <ThumbsUp className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : null;
          })()}

          {/* Add new keyword */}
          <div className="flex items-center gap-2">
            <input
              value={newKeywordText}
              onChange={e => setNewKeywordText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleAddKeyword(); }}
              placeholder={t("admin.placeholder.keyword")}
              className="flex-1 text-sm border border-border rounded-lg px-3 py-1.5 bg-background"
            />
            <select
              value={newKeywordWeight}
              onChange={e => setNewKeywordWeight(parseFloat(e.target.value))}
              className="text-xs border border-border rounded px-2 py-1.5 bg-background text-foreground w-16"
              title={t("admin.label.weight")}
            >
              <option value={0.5}>0.5</option>
              <option value={1.0}>1.0</option>
              <option value={1.5}>1.5</option>
              <option value={2.0}>2.0</option>
              <option value={3.0}>3.0</option>
            </select>
            <button onClick={handleAddKeyword}
              disabled={!newKeywordText.trim()}
              className="flex items-center gap-1 text-sm bg-primary text-primary-foreground rounded-lg px-3 py-1.5 disabled:opacity-50">
              <Plus className="w-4 h-4" /> {t("admin.btn.add")}
            </button>
          </div>
        </section>

        {/* Moduly */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">{t("admin.section.modules")}</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {modules.map(m => (
              <div key={m.key} className={`rounded-xl border p-4 ${m.badgeCls}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{m.icon}</span>
                  <span className="font-semibold text-sm">{m.label}</span>
                </div>
                <p className="text-xs opacity-80">{m.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Nabídkový formulář */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">
              {t("admin.section.quoteForm")}
            </h2>
            {quoteConfig && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                isApproved
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
                  : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
              }`}>
                {isApproved ? t("admin.label.formApprovedBadge") : t("admin.label.formDraftBadge")}
              </span>
            )}
          </div>

          {/* No config */}
          {!quoteConfig && (
            <div className="rounded-xl border border-dashed border-muted-foreground/30 bg-muted/10 text-center py-10 space-y-3">
              <p className="text-sm text-muted-foreground">{t("admin.section.noQuoteForm")}</p>
              <button onClick={handleGenerateQuoteConfig} disabled={generatingConfig}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
                {generatingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                {generatingConfig ? t("admin.label.generating") : t("admin.label.generateForm")}
              </button>
              {isFasadyService && (
                <p className="text-[10px] text-muted-foreground italic">
                  {t("admin.label.facadeNote")}
                </p>
              )}
            </div>
          )}

          {/* Draft editing */}
          {isEditing && (
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
              {/* Cost fields editor */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">{t("admin.section.costItems")}</p>
                {fields.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 rounded-lg border border-border bg-background p-2.5">
                    <input value={f.icon}
                      onChange={(e) => { const u = [...fields]; u[idx] = { ...u[idx], icon: e.target.value.slice(0, 4) }; setEditingConfigFields(u); }}
                      className="w-10 text-center text-lg bg-transparent border-none outline-none" />
                    <input value={f.label}
                      onChange={(e) => { const u = [...fields]; u[idx] = { ...u[idx], label: e.target.value }; setEditingConfigFields(u); }}
                      className="flex-1 min-w-0 text-sm px-2 py-1 rounded border border-border bg-background text-foreground outline-none focus:ring-1 focus:ring-ring"
                      placeholder={t("admin.placeholder.itemName")} />
                    <input value={f.hint || ""}
                      onChange={(e) => { const u = [...fields]; u[idx] = { ...u[idx], hint: e.target.value || null }; setEditingConfigFields(u); }}
                      className="w-44 text-xs px-2 py-1 rounded border border-border bg-background text-muted-foreground outline-none focus:ring-1 focus:ring-ring"
                      placeholder={t("admin.placeholder.itemName")} />
                    <button onClick={() => setEditingConfigFields(fields.filter((_, i) => i !== idx))}
                      className="p-1 text-muted-foreground hover:text-destructive transition-colors shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button onClick={() => setEditingConfigFields([...fields, { key: `custom_${Date.now()}`, label: "", icon: "📋", hint: null }])}
                  className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Plus className="w-3 h-3" /> {t("admin.btn.addItem")}
                </button>
              </div>

              {/* Options row */}
              <div className="flex flex-wrap items-center gap-5 pt-3 border-t border-border">
                <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer">
                  <input type="checkbox" checked={editingConfigShowProfit} onChange={(e) => setEditingConfigShowProfit(e.target.checked)} className="rounded" />
                  {t("admin.label.profit")}
                </label>
                <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer">
                  <input type="checkbox" checked={editingConfigShowVat} onChange={(e) => setEditingConfigShowVat(e.target.checked)} className="rounded" />
                  {t("admin.label.vat")}
                </label>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">{t("admin.label.unitLabel")}</span>
                  <select value={editingConfigUnitLabel} onChange={(e) => setEditingConfigUnitLabel(e.target.value)}
                    className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground">
                    <option value="m²">{"m²"}</option>
                    <option value="bm">{"bm"}</option>
                    <option value="ks">{"ks"}</option>
                    <option value="hod">{"hod"}</option>
                    <option value="komplet">{"komplet"}</option>
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className="rounded-xl border border-dashed border-muted-foreground/30 bg-muted/10 p-4 space-y-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">{t("admin.section.formPreview")}</p>
                {fields.filter(f => f.label.trim()).map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span>{f.icon}</span>
                    <span className="text-foreground">{f.label}</span>
                    <span className="flex-1 border-b border-dotted border-muted-foreground/30" />
                    <span className="text-muted-foreground text-xs">{"___ "}{t("admin.label.currency")}</span>
                  </div>
                ))}
                {editingConfigShowProfit && (
                  <div className="flex items-center gap-2 text-sm pt-1 border-t border-border">
                    <span>{"💰"}</span>
                    <span className="text-foreground">{t("admin.label.profit")}</span>
                    <span className="flex-1 border-b border-dotted border-muted-foreground/30" />
                    <span className="text-muted-foreground text-xs">{"___ "}{t("admin.label.currency")}</span>
                  </div>
                )}
                {editingConfigShowVat && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                    <span>{"🧾"}</span>
                    <span>{t("admin.label.vatRate")}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm font-semibold pt-1 border-t border-border">
                  <span>{"🏷️"}</span>
                  <span className="text-foreground">{t("admin.label.totalPrice")}</span>
                  <span className="flex-1" />
                  <span className="text-foreground">{"___ "}{t("admin.label.currency")}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <button onClick={handleSaveQuoteConfig} disabled={savingConfig}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm hover:bg-muted transition-colors disabled:opacity-50">
                  <Save className="w-4 h-4" /> {t("admin.btn.saveDraft")}
                </button>
                <button onClick={handleApproveQuoteConfig} disabled={savingConfig || fields.filter(f => f.label.trim()).length === 0}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50">
                  {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                  {t("admin.btn.approveAndDeploy")}
                </button>
                <button onClick={handleGenerateQuoteConfig} disabled={generatingConfig}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50 ml-auto">
                  <Bot className="w-3.5 h-3.5" /> {t("admin.btn.regenerate")}
                </button>
              </div>
            </div>
          )}

          {/* Approved — read-only */}
          {isApproved && quoteConfig && (
            <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50/40 dark:bg-green-950/10 p-5 space-y-4">
              <div className="rounded-lg border border-border bg-card p-4 space-y-2">
                {quoteConfig.cost_fields.map((f: CostField, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span>{f.icon}</span>
                    <span className="font-medium text-foreground">{f.label}</span>
                    {f.hint && <span className="text-xs text-muted-foreground">({f.hint})</span>}
                  </div>
                ))}
                <div className="flex gap-4 text-xs text-muted-foreground pt-2 border-t border-border">
                  {quoteConfig.show_profit && <span>{"💰 "}{t("admin.label.profit")}</span>}
                  {quoteConfig.show_vat && <span>{"🧾 "}{t("admin.label.vat")}</span>}
                  <span>{"📐 "}{t("admin.label.unitLabel")}{" "}{quoteConfig.unit_label}</span>
                </div>
              </div>
              <button onClick={handleUnapproveQuoteConfig}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Save className="w-4 h-4" /> {t("admin.btn.editForm")}
              </button>
            </div>
          )}
        </section>

        {/* Znalosti AI */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
              <Brain className="w-4 h-4" />
              {t("admin.section.knowledgeFor")}{" "}{service.name}
            </h2>
            <span className="text-xs text-muted-foreground">
              {learnings.length}{" "}{learnings.length === 1 ? t("admin.label.record1") : learnings.length < 5 ? t("admin.label.record2to4") : t("admin.label.records")}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("admin.section.aiKnowledge")}
          </p>

          {/* Existing learnings */}
          {learnings.length > 0 && (
            <div className="space-y-2">
              {learnings.map(l => (
                <div key={l.id} className={`rounded-xl border p-4 ${l.is_active ? "border-border bg-card" : "border-border/50 bg-muted/30 opacity-60"}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-lg shrink-0 mt-0.5">{l.type === "correction" ? "🔧" : l.type === "fact" ? "📚" : "💡"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm text-foreground">{l.topic}</p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{l.type}</span>
                      </div>
                      <p className={`text-xs text-muted-foreground ${expandedLearning === l.id ? "" : "line-clamp-2"}`}>{l.content}</p>
                      {l.content.length > 120 && (
                        <button onClick={() => setExpandedLearning(expandedLearning === l.id ? null : l.id)}
                          className="text-[10px] text-primary hover:underline mt-1">
                          {expandedLearning === l.id ? t("admin.btn.collapse") : t("admin.btn.showAll")}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleToggleLearning(l)}
                        className={`text-xs px-2 py-1 rounded ${l.is_active ? "text-green-600 hover:text-amber-600 bg-green-50" : "text-muted-foreground hover:text-green-600 bg-muted"}`}
                        title={l.is_active ? t("admin.toast.deactivated") : t("admin.toast.activated")}>
                        {l.is_active ? t("admin.label.learningActive") : t("admin.label.learningInactive")}
                      </button>
                      <button onClick={() => handleDeleteLearning(l.id)}
                        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add new learning */}
          {showNewLearning ? (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
              <div className="flex gap-2">
                <select value={newLearningType} onChange={(e) => setNewLearningType(e.target.value)}
                  className="text-sm border border-border rounded px-2.5 py-1.5 bg-background text-foreground">
                  <option value="fact">{t("admin.label.fact")}</option>
                  <option value="correction">{t("admin.label.correction")}</option>
                  <option value="tip">{t("admin.label.tip")}</option>
                </select>
                <input value={newLearningTopic} onChange={(e) => setNewLearningTopic(e.target.value)}
                  placeholder={t("admin.placeholder.learningTopic")}
                  className="flex-1 text-sm px-3 py-1.5 rounded border border-border bg-background text-foreground outline-none focus:ring-1 focus:ring-ring" />
              </div>
              <textarea value={newLearningContent} onChange={(e) => setNewLearningContent(e.target.value)}
                placeholder={t("admin.placeholder.learningContent")}
                rows={4}
                className="w-full text-sm px-3 py-2 rounded border border-border bg-background text-foreground outline-none focus:ring-1 focus:ring-ring resize-none" />
              <div className="flex gap-2">
                <button onClick={handleCreateLearning}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
                  <Plus className="w-4 h-4" /> {t("admin.btn.save")}
                </button>
                <button onClick={() => { setShowNewLearning(false); setNewLearningTopic(""); setNewLearningContent(""); }}
                  className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground">
                  {t("admin.btn.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => { setShowNewLearning(true); setNewLearningTopic(""); setNewLearningContent(""); }}
              className="flex items-center gap-2 text-sm text-primary font-medium hover:text-primary/80 transition-colors">
              <Plus className="w-4 h-4" /> {t("admin.btn.addKnowledge")}{" "}{service.name}
            </button>
          )}
        </section>

        {/* Správa stavu služby */}
        <section className="space-y-3 pt-4 border-t border-border">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t("admin.section.serviceStatus")}</h2>
          <div className="flex items-center gap-3 flex-wrap">
            {service.status !== "active" && (
              <button onClick={() => handleServiceStatus("active")}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors">
                <ThumbsUp className="w-4 h-4" /> {t("admin.btn.activate")}
              </button>
            )}
            {service.status !== "queued" && (
              <button onClick={() => handleServiceStatus("queued")}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted transition-colors">
                {t("admin.btn.toQueue")}
              </button>
            )}
            {service.status !== "rejected" && (
              <button onClick={() => handleServiceStatus("rejected")}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-sm text-red-600 hover:bg-red-50 transition-colors">
                {t("admin.btn.reject")}
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
